// Schema-driven compare table. Extracted from ProjectsPage.jsx's inline
// {tab === 'compare' && (...)} block. Rendering goes through the schema
// (frontend/src/config/displaySchema.js) so the same field appears here,
// in the SelectedSiteCard, and in the AnalysisPanel with identical text
// and colour — renderCell() is the single owner of cls (block > warn > '').
//
// Kept from the current ProjectsPage:
//   - Sticky header row + sticky first column (via max-height on .ct-scroll)
//   - Remove-site × on each column header
//   - ⓘ tooltip on each row label (via ProvenanceIcon)
//
// Not yet ported (follow-up commit — headers pointing at fixups):
//   - Verdict row using <ScoreCell> (top row above every group)
//   - Grid-queue row using <GridQueueCell> with the ▾ expander
//   Placeholder: tier renders in the site-header cell for now.
//
// Moved out:
//   - Differences-only toggle → header slot inside this component (was on
//     the tab bar). Comparison is on cell.num (numeric, at display
//     precision) not on the formatted string, so display-format nudges
//     don't create fake differences. "Not assessed" counts as a difference.
//   - Export PDF / XLSX / Refresh all / + Add site → out to ProjectsV2 shell.
//
// Props:
//   projects        SiteProject[]    — already fetched, org-scoped
//   selectedSiteId  string | null    — id of the highlighted column
//   onSelectSite    (id) => void     — click a column header to select
//   onRemoveSite    (id) => void     — × handler; hide button if omitted
//   mode            'datacenter' | 'hydrogen'  — required; no default here

import React, { useMemo, useState } from 'react';
import { bestIndex, compareFieldsFor, groupsFor, groupIdFor, renderCell } from '../../config/displaySchema';
import { tierForIndex } from '../SeidrIndex';
import { ProvenanceIcon } from '../ProvenancePopover';
import { FIELD_PROVENANCE } from '../../utils/fieldProvenance';
import '../../styles/CompareTable.css';

export default function CompareTable({
  projects,
  selectedSiteId,
  onSelectSite,
  onRemoveSite,
  mode,
}) {
  const [diffOnly, setDiffOnly] = useState(true);

  // Which fields go into the table for this set of projects. A `showWhen:
  // flagged` field appears if ANY compared site trips its warn/block.
  const fields = useMemo(() => compareFieldsFor(projects, mode), [projects, mode]);

  // Group by schema group.id, preserving GROUPS order.
  const rowsByGroup = useMemo(() => {
    const out = new Map();
    for (const g of groupsFor(mode)) out.set(g.id, { group: g, rows: [] });
    for (const f of fields) {
      const bucket = out.get(groupIdFor(f, mode));
      if (bucket) bucket.rows.push(f);
    }
    return Array.from(out.values()).filter(x => x.rows.length > 0);
  }, [fields, mode]);

  // Precompute cells for every (field × project) pair once. renderCell is
  // the single source of cls (block > warn > 'na' > '').
  const grid = useMemo(() => {
    const g = new Map();
    for (const f of fields) {
      g.set(f.key, projects.map(p => renderCell(f, p)));
    }
    return g;
  }, [fields, projects]);

  // Best-in-row (green dot): displaySchema.bestIndex, shared with the
  // exports. No dot when the best value is shared by 2+ sites.
  function bestIndices(field) {
    const i = bestIndex(field, grid.get(field.key));
    return i >= 0 ? new Set([i]) : new Set();
  }

  // "Identical across sites" uses cell.num when present, otherwise cell.text.
  // "Not assessed" (cls 'na') always counts as a difference — even when two
  // sites both read Not assessed, keep the row visible.
  function isIdenticalAcrossSites(field) {
    const cells = grid.get(field.key);
    if (cells.length < 2) return true;
    if (cells.some(c => c.cls === 'na')) return false;
    const key = c => c.num != null ? `n:${c.num}` : `t:${c.text}`;
    const first = key(cells[0]);
    return cells.every(c => key(c) === first);
  }

  const identicalCount = useMemo(() => {
    return fields.filter(isIdenticalAcrossSites).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, projects]);

  if (projects.length === 0) return null;

  return (
    <div className="ct-wrap">
      <div className="ct-tools">
        <label className="ct-toggle">
          <input
            type="checkbox"
            checked={diffOnly}
            onChange={(e) => setDiffOnly(e.target.checked)}
          />
          Differences only
        </label>
        {diffOnly && identicalCount > 0 && (
          <span className="ct-hidden-note">
            {identicalCount} row{identicalCount === 1 ? '' : 's'} identical across sites
          </span>
        )}
      </div>

      <div className="ct-scroll">
        <table className="ct-table">
          <thead>
            <tr>
              <th className="ct-rowhead ct-th">Site</th>
              {projects.map(p => {
                const isSel = p.id === selectedSiteId;
                const pct = p.readiness_score;
                const tier = pct != null ? tierForIndex(pct) : null;
                return (
                  <th key={p.id} className={`ct-th ct-site-th ${isSel ? 'ct-th-selected' : ''}`}>
                    <button
                      className="ct-site-header-btn"
                      onClick={() => onSelectSite?.(p.id)}
                      title="Select this site"
                    >
                      <div className="ct-site-label">{p.site_label}</div>
                      {tier && (
                        <div className="ct-site-tier" style={{ color: tier.color }}>
                          <span className="ct-site-score">{Math.round(pct)}</span> · {tier.tier}
                        </div>
                      )}
                    </button>
                    {onRemoveSite && (
                      <button
                        className="ct-site-remove"
                        onClick={(e) => { e.stopPropagation(); onRemoveSite(p.id); }}
                        title="Drop this site"
                      >×</button>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rowsByGroup.map(({ group, rows }) => {
              // Rows that are "Not assessed" for EVERY site collapse into the
              // footer line below instead of a row of empty cells.
              const assessedSomewhere = f => (grid.get(f.key) || []).some(c => c.cls !== 'na');
              const visibleRows = (diffOnly ? rows.filter(f => !isIdenticalAcrossSites(f)) : rows).filter(assessedSomewhere);
              if (!visibleRows.length) return null;
              return (
                <React.Fragment key={group.id}>
                  <tr className="ct-group-row">
                    <td colSpan={1 + projects.length}>{group.label}</td>
                  </tr>
                  {visibleRows.map(f => {
                    const cells = grid.get(f.key);
                    const best = bestIndices(f);
                    const meta = FIELD_PROVENANCE?.[f.key];
                    return (
                      <tr key={f.key}>
                        <td className="ct-rowhead">
                          {f.label}
                          {meta && (
                            <ProvenanceIcon field={f.key} label={f.label} />
                          )}
                        </td>
                        {cells.map((c, i) => {
                          const clsBits = [];
                          if (c.cls === 'warn')  clsBits.push('ct-warn');
                          if (c.cls === 'block') clsBits.push('ct-block');
                          if (c.cls === 'na')    clsBits.push('ct-na');
                          if (best.has(i))       clsBits.push('ct-best');
                          if (projects[i].id === selectedSiteId) clsBits.push('ct-col-selected');
                          return (
                            <td key={projects[i].id} className={clsBits.join(' ')}>
                              <div className="ct-cell-text">{c.text}</div>
                              {c.meta && <div className="ct-cell-meta">{c.meta}</div>}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {(() => {
          const missing = fields.filter(f => (grid.get(f.key) || []).every(c => c.cls === 'na')).map(f => f.label);
          if (!missing.length) return null;
          return (
            <div className="ct-footnote" style={{ fontSize: 11, color: '#94a3b8', padding: '8px 4px' }}>
              Not yet assessed for any site: {missing.join(', ')}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
